export interface ContentMarketingScores {
  strategyPlanning: number;
  contentCreation: number;
  distributionEngagement: number;
}

export interface ContentMarketingResult {
  questionId: number;
  scores: ContentMarketingScores;
}

export interface Question {
  id: number;
  question: string;
  options: {
    text: string;
    scores: ContentMarketingScores;
  }[];
}

export const questions: Question[] = [
  {
    id: 1,
    question: "Do you have a documented content marketing strategy aligned with your business goals?",
    options: [
      {
        text: "Yes - comprehensive strategy with clear goals, audience personas, and KPIs",
        scores: { strategyPlanning: 1, contentCreation: 1, distributionEngagement: 1 }
      },
      {
        text: "Partially - we have some goals but no formal documented strategy",
        scores: { strategyPlanning: 2, contentCreation: 2, distributionEngagement: 1 }
      },
      {
        text: "Minimal - we create content occasionally without a clear plan",
        scores: { strategyPlanning: 3, contentCreation: 2, distributionEngagement: 2 }
      },
      {
        text: "No - we post content randomly without strategic planning",
        scores: { strategyPlanning: 3, contentCreation: 3, distributionEngagement: 3 }
      }
    ]
  },
  {
    id: 2,
    question: "How frequently do you publish high-quality, relevant content for your Saskatchewan audience?",
    options: [
      {
        text: "Consistently - following a regular content calendar with weekly/bi-weekly posts",
        scores: { strategyPlanning: 1, contentCreation: 1, distributionEngagement: 1 }
      },
      {
        text: "Occasionally - we publish when we have time, a few times per month",
        scores: { strategyPlanning: 2, contentCreation: 2, distributionEngagement: 2 }
      },
      {
        text: "Rarely - only a few times per year when something important happens",
        scores: { strategyPlanning: 3, contentCreation: 3, distributionEngagement: 2 }
      },
      {
        text: "Never - we haven't published new content in months or years",
        scores: { strategyPlanning: 3, contentCreation: 3, distributionEngagement: 3 }
      }
    ]
  },
  {
    id: 3,
    question: "What types of content formats do you currently create?",
    options: [
      {
        text: "Diverse mix - blog posts, videos, infographics, podcasts, case studies",
        scores: { strategyPlanning: 1, contentCreation: 1, distributionEngagement: 1 }
      },
      {
        text: "Multiple formats - primarily blog posts with some videos or images",
        scores: { strategyPlanning: 1, contentCreation: 2, distributionEngagement: 1 }
      },
      {
        text: "Limited - mostly just text-based blog posts or social media updates",
        scores: { strategyPlanning: 2, contentCreation: 3, distributionEngagement: 2 }
      },
      {
        text: "Minimal - we rarely create any original content",
        scores: { strategyPlanning: 3, contentCreation: 3, distributionEngagement: 3 }
      }
    ]
  },
  {
    id: 4,
    question: "How well does your content address your Saskatchewan customers' pain points and questions?",
    options: [
      {
        text: "Excellent - we research and create content based on customer needs and searches",
        scores: { strategyPlanning: 1, contentCreation: 1, distributionEngagement: 1 }
      },
      {
        text: "Good - we try to address customer questions but not systematically",
        scores: { strategyPlanning: 2, contentCreation: 2, distributionEngagement: 1 }
      },
      {
        text: "Poor - our content is mostly about us, not customer problems",
        scores: { strategyPlanning: 3, contentCreation: 3, distributionEngagement: 2 }
      },
      {
        text: "Unknown - we haven't researched what our customers need",
        scores: { strategyPlanning: 3, contentCreation: 3, distributionEngagement: 3 }
      }
    ]
  },
  {
    id: 5,
    question: "How do you distribute and promote your content across channels?",
    options: [
      {
        text: "Multi-channel strategy - website, email, social media, SEO optimized",
        scores: { strategyPlanning: 1, contentCreation: 1, distributionEngagement: 1 }
      },
      {
        text: "Basic distribution - we post to our website and maybe one social channel",
        scores: { strategyPlanning: 1, contentCreation: 2, distributionEngagement: 2 }
      },
      {
        text: "Limited - content sits on our website with minimal promotion",
        scores: { strategyPlanning: 2, contentCreation: 2, distributionEngagement: 3 }
      },
      {
        text: "No distribution plan - we publish and hope people find it",
        scores: { strategyPlanning: 3, contentCreation: 3, distributionEngagement: 3 }
      }
    ]
  },
  {
    id: 6,
    question: "Are you measuring content performance and using data to improve results?",
    options: [
      {
        text: "Yes - we track metrics, analyze performance, and optimize based on data",
        scores: { strategyPlanning: 1, contentCreation: 1, distributionEngagement: 1 }
      },
      {
        text: "Sometimes - we check basic metrics like views but don't optimize regularly",
        scores: { strategyPlanning: 2, contentCreation: 2, distributionEngagement: 2 }
      },
      {
        text: "Rarely - we might look at stats occasionally but don't act on them",
        scores: { strategyPlanning: 2, contentCreation: 3, distributionEngagement: 3 }
      },
      {
        text: "No - we don't track content performance or ROI",
        scores: { strategyPlanning: 3, contentCreation: 3, distributionEngagement: 3 }
      }
    ]
  },
  {
    id: 7,
    question: "How engaged is your audience with your content?",
    options: [
      {
        text: "Highly engaged - consistent comments, shares, leads, and conversions",
        scores: { strategyPlanning: 1, contentCreation: 1, distributionEngagement: 1 }
      },
      {
        text: "Moderately engaged - some interaction but could be better",
        scores: { strategyPlanning: 1, contentCreation: 2, distributionEngagement: 2 }
      },
      {
        text: "Low engagement - minimal comments, shares, or actions taken",
        scores: { strategyPlanning: 2, contentCreation: 3, distributionEngagement: 3 }
      },
      {
        text: "No engagement - our content gets little to no response",
        scores: { strategyPlanning: 3, contentCreation: 3, distributionEngagement: 3 }
      }
    ]
  }
];
